import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, FileText, AlertTriangle, 
  CheckCircle2, XCircle, Loader2, Download, Info
} from 'lucide-react';
import api from '../lib/api';

const STATUS_CONFIG = {
  new: {
    color: 'bg-green-500/10 text-green-600 border-green-500/20',
    icon: CheckCircle2,
    label: { fr: 'Nouvelle', en: 'New', pt: 'Nova' }
  },
  possible_duplicate: {
    color: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    icon: AlertTriangle,
    label: { fr: 'Doublon possible', en: 'Possible duplicate', pt: 'Possível duplicata' }
  },
  duplicate: {
    color: 'bg-red-500/10 text-red-600 border-red-500/20',
    icon: XCircle,
    label: { fr: 'Doublon', en: 'Duplicate', pt: 'Duplicata' }
  }
};

export default function BankImportModal({ open, onOpenChange, onImportComplete }) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  const [step, setStep] = useState('upload'); // 'upload', 'preview', 'importing'
  const [targetMonth, setTargetMonth] = useState('current');
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const fileName = selectedFile.name.toLowerCase();
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        toast.error(t('invalid_file_format') || 'Format de fichier invalide. Utilisez PDF ou Excel.');
        return;
      }
      setFile(selectedFile);
    }
  }, [t]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const fileName = droppedFile.name.toLowerCase();
      if (!fileName.endsWith('.pdf') && !fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        toast.error(t('invalid_file_format') || 'Format de fichier invalide. Utilisez PDF ou Excel.');
        return;
      }
      setFile(droppedFile);
    }
  }, [t]);

  const handlePreview = async () => {
    if (!file || !user) return;
    
    setIsLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_month', targetMonth);
      formData.append('user_id', user.user_id);
      
      const response = await api.post('/bank-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreviewData(response.data);
      setTransactions(response.data.transactions || []);
      setStep('preview');
      
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || t('import_preview_error') || 'Erreur lors de l\'analyse du fichier');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleTransaction = (index) => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], selected: !updated[index].selected };
      return updated;
    });
  };

  const handleToggleAll = (selected) => {
    setTransactions(prev => prev.map(tx => ({ ...tx, selected })));
  };

  const handleCategoryChange = (index, category) => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], suggested_category: category };
      return updated;
    });
  };

  const handleImport = async () => {
    const selectedTransactions = transactions.filter(tx => tx.selected);
    
    if (selectedTransactions.length === 0) {
      toast.error(t('no_transactions_selected') || 'Aucune transaction sélectionnée');
      return;
    }
    
    setStep('importing');
    setIsLoading(true);
    
    try {
      const response = await api.post('/bank-import/import', {
        transactions: transactions,
        target_month: targetMonth
      }, {
        params: { user_id: user.user_id }
      });
      
      toast.success(
        `${response.data.imported_count} ${t('transactions_imported') || 'transactions importées'}`
      );
      
      onImportComplete?.();
      handleClose();
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || t('import_error') || 'Erreur lors de l\'import');
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setPreviewData(null);
    setTransactions([]);
    onOpenChange(false);
  };

  const selectedCount = transactions.filter(tx => tx.selected).length;
  const categories = [
    'Alimentation', 'Transport', 'Logement', 'Télécom', 'Santé',
    'Loisirs', 'Shopping', 'Assurance', 'Banque', 'Enfants',
    'Restaurant', 'Abonnements', 'Éducation', 'Cadeaux', 'Voyages', 'Autre'
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" />
            {t('import_bank_statement') || 'Importer un extrait bancaire'}
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && (t('import_bank_desc') || 'Importez vos dépenses depuis un fichier PDF ou Excel')}
            {step === 'preview' && (t('import_preview_desc') || 'Vérifiez et sélectionnez les transactions à importer')}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="space-y-4 py-4">
            {/* File format info */}
            <div className="flex items-start gap-2 p-3 bg-blue-500/10 rounded-lg text-sm">
              <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-700 dark:text-blue-300">
                  {t('supported_formats') || 'Formats supportés'}
                </p>
                <p className="text-muted-foreground">
                  PDF (Banque Populaire, etc.) • Excel (.xlsx)
                </p>
              </div>
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-colors
                ${file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
              `}
            >
              {file ? (
                <div className="space-y-2">
                  {file.name.endsWith('.pdf') ? (
                    <FileText className="w-12 h-12 mx-auto text-red-500" />
                  ) : (
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-green-500" />
                  )}
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setFile(null)}
                  >
                    {t('change_file') || 'Changer de fichier'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                  <p className="font-medium">
                    {t('drop_file_here') || 'Glissez votre fichier ici'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t('or') || 'ou'}
                  </p>
                  <div>
                    <input
                      id="bank-file-input"
                      type="file"
                      accept=".pdf,.xlsx,.xls"
                      onChange={handleFileSelect}
                      className="sr-only"
                      data-testid="bank-file-input"
                    />
                    <label 
                      htmlFor="bank-file-input"
                      className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer"
                      data-testid="bank-file-select-btn"
                    >
                      {t('select_file') || 'Sélectionner un fichier'}
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Target month */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('target_month') || 'Mois cible'}
              </label>
              <Select value={targetMonth} onValueChange={setTargetMonth}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">
                    {t('current_month') || 'Mois en cours'}
                  </SelectItem>
                  <SelectItem value="previous">
                    {t('previous_month') || 'Mois précédent'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                {t('cancel')}
              </Button>
              <Button 
                className="flex-1" 
                onClick={handlePreview}
                disabled={!file || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('analyzing') || 'Analyse...'}
                  </>
                ) : (
                  t('analyze') || 'Analyser'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && previewData && (
          <div className="flex flex-col flex-1 min-h-0 space-y-4 py-2">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-lg bg-green-500/10">
                <p className="text-2xl font-bold text-green-600">{previewData.new_transactions}</p>
                <p className="text-xs text-muted-foreground">{t('new') || 'Nouvelles'}</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-500/10">
                <p className="text-2xl font-bold text-amber-600">{previewData.possible_duplicates}</p>
                <p className="text-xs text-muted-foreground">{t('possible_duplicates') || 'Doublons possibles'}</p>
              </div>
              <div className="p-2 rounded-lg bg-red-500/10">
                <p className="text-2xl font-bold text-red-600">{previewData.duplicates}</p>
                <p className="text-xs text-muted-foreground">{t('duplicates') || 'Doublons'}</p>
              </div>
            </div>

            {/* Select all */}
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={selectedCount === transactions.length}
                  onCheckedChange={(checked) => handleToggleAll(!!checked)}
                />
                <span className="text-sm font-medium">
                  {selectedCount} / {transactions.length} {t('selected') || 'sélectionnées'}
                </span>
              </div>
              <div className="flex gap-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleToggleAll(true)}
                >
                  {t('select_all') || 'Tout'}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setTransactions(prev => 
                    prev.map(tx => ({ ...tx, selected: tx.status === 'new' }))
                  )}
                >
                  {t('select_new_only') || 'Nouvelles'}
                </Button>
              </div>
            </div>

            {/* Transaction list */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-2 min-h-0">
              {transactions.map((tx, index) => {
                const statusConfig = STATUS_CONFIG[tx.status];
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div 
                    key={index}
                    className={`
                      p-3 rounded-lg border transition-colors
                      ${tx.selected ? 'bg-primary/5 border-primary/30' : 'bg-card border-border'}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        checked={tx.selected}
                        onCheckedChange={() => handleToggleTransaction(index)}
                        className="mt-1"
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">
                            {tx.merchant_name || tx.description?.substring(0, 30)}
                          </span>
                          <Badge variant="outline" className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label[language] || statusConfig.label.fr}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <span>{tx.date}</span>
                          <span>•</span>
                          <span className="font-medium text-foreground">
                            {tx.amount.toFixed(2)} €
                          </span>
                        </div>
                        
                        {tx.duplicate_reason && tx.status !== 'new' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {tx.duplicate_reason}
                          </p>
                        )}
                      </div>
                      
                      {/* Category selector */}
                      <Select 
                        value={tx.suggested_category || 'Autre'}
                        onValueChange={(val) => handleCategoryChange(index, val)}
                      >
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Button variant="outline" className="flex-1" onClick={() => setStep('upload')}>
                {t('back')}
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleImport}
                disabled={selectedCount === 0 || isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('importing') || 'Import...'}
                  </>
                ) : (
                  <>
                    {t('import')} ({selectedCount})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <p className="mt-4 font-medium">
              {t('importing_transactions') || 'Import des transactions en cours...'}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
